import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css'; // Reuse existing styles

function Widget() {
  const [dateRange, setDateRange] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [contentData] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [blockedDates, setBlockedDates] = useState({});
  const [monthAvailability, setMonthAvailability] = useState({});
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [roomImages, setRoomImages] = useState({});
  const calendarRef = useRef(null);

  // Get widget configuration from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const widgetConfig = {
    bbid: urlParams.get('bbid') || '29515',
    theme: urlParams.get('theme') || 'light',
    primaryColor: '#' + (urlParams.get('color') || '2997dc'),
    embedded: urlParams.get('embedded') === 'true'
  };

  // Dynamic configuration based on widget BBID
  const config = {
    datePicker: {
      title: "Select Date Range",
      placeholder: "Select date range...",
      propertyBBIDs: [widgetConfig.bbid],
    },
    api: {
      availabilityWorker: "https://checkavailability.alldigital.workers.dev",
      contentWorker: "https://getcontent.alldigital.workers.dev",
      imageWorker: "https://getimages.alldigital.workers.dev",
    },
    booking: {
      propertyId: "20001",
      nbid: "371",
      baseUrl: "https://book.nightsbridge.com"
    },
    header: {
      logo: "Check Availability"
    }
  };

  // Set default dates (next weekend)
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const nextWeekend = new Date(today);
    nextWeekend.setDate(today.getDate() + (7 - today.getDay() + 5) % 7);
    nextWeekend.setHours(0, 0, 0, 0);
    
    const endDate = new Date(nextWeekend);
    endDate.setDate(nextWeekend.getDate() + 7);
    endDate.setHours(0, 0, 0, 0);
    
    setSelectedStartDate(nextWeekend);
    setSelectedEndDate(endDate);
    setDateRange(`${nextWeekend.getFullYear()}-${String(nextWeekend.getMonth() + 1).padStart(2, '0')}-${String(nextWeekend.getDate()).padStart(2, '0')} - ${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`);
  }, []);

  // Apply theme-based styling
  useEffect(() => {
    const root = document.documentElement;
    if (widgetConfig.theme === 'dark') {
      root.style.setProperty('--primary-color', widgetConfig.primaryColor);
      root.style.setProperty('--bg-color', '#1f2937');
      root.style.setProperty('--text-color', '#f9fafb');
      root.style.setProperty('--card-bg', '#374151');
    } else {
      root.style.setProperty('--primary-color', widgetConfig.primaryColor);
      root.style.setProperty('--bg-color', '#ffffff');
      root.style.setProperty('--text-color', '#374151');
      root.style.setProperty('--card-bg', '#ffffff');
    }
  }, [widgetConfig]);

  // Auto-resize iframe height for embedded widgets
  useEffect(() => {
    if (widgetConfig.embedded) {
      const resizeObserver = new ResizeObserver(() => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({
          type: 'widget-resize',
          height: height
        }, '*');
      });
      
      resizeObserver.observe(document.body);
      
      return () => resizeObserver.disconnect();
    }
  }, [widgetConfig.embedded]);

  // [Include all the existing functions from App.js - fetchAvailability, handleDateClick, etc.]
  // For brevity, I'll include the key ones:

  const fetchAvailability = useCallback(async (start, end) => {
    if (!start || !end || isFetching) {
      console.log('Skipping fetch - missing dates or already fetching');
      return;
    }

    console.log('Starting availability fetch for:', start, 'to', end);
    setIsFetching(true);
    setIsLoadingAvailability(true);
    setError(null);

    try {
      const bbids = [widgetConfig.bbid];
      console.log('Checking availability for BBID:', widgetConfig.bbid);
      
      const requestBody = {
        bbid: widgetConfig.bbid,
        startdate: start,
        enddate: end,
        includepackages: true,
        strictsearch: false
      };

      console.log(`Sending availability request:`, requestBody);
      
      const response = await fetch(config.api.availabilityWorker, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log(`Availability Response status:`, response.status);
      
      const data = await response.json();
      console.log(`Availability data received:`, data);
      
      if (data.success && data.data?.roomtypes) {
        const combinedData = {
          ...data,
          data: {
            ...data.data,
            roomtypes: data.data.roomtypes.map(room => ({
              ...room,
              bbid: widgetConfig.bbid,
              propertyName: data.data.name
            }))
          }
        };
        
        console.log('Combined availability data:', combinedData);
        setAvailabilityData(combinedData);
        
        // Fetch room images
        try {
          console.log('Fetching room images for BBID:', widgetConfig.bbid);
          
          const imageResponse = await fetch(config.api.imageWorker, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              bblist: [parseInt(widgetConfig.bbid)]
            })
          });

          console.log(`Image API Response status:`, imageResponse.status);
          
          if (imageResponse.ok) {
            const imageData = await imageResponse.json();
            console.log(`Image API Response data:`, imageData);
            
            if (imageData.success && imageData.data) {
              const combinedImageData = {};
              
              // Check for roomtypegalleries first (expected format)
              if (imageData.data?.roomtypegalleries) {
                imageData.data.roomtypegalleries.forEach(roomGallery => {
                  if (roomGallery.roomtypeid && roomGallery.gallery && roomGallery.gallery.length > 0) {
                    const roomtypeid = String(roomGallery.roomtypeid);
                    if (!combinedImageData[roomtypeid]) {
                      const firstImage = roomGallery.gallery[0];
                      combinedImageData[roomtypeid] = [firstImage];
                      console.log(`Added image for roomtype ${roomtypeid}`);
                    }
                  }
                });
              } 
              // Check for imageslist format (actual API response)
              else if (imageData.data?.imageslist) {
                imageData.data.imageslist.forEach((item, index) => {
                  if (item.roomtypegalleries && Array.isArray(item.roomtypegalleries)) {
                    item.roomtypegalleries.forEach(roomGallery => {
                      if (roomGallery.roomtypeid && roomGallery.gallery && roomGallery.gallery.length > 0) {
                        const roomtypeid = String(roomGallery.roomtypeid);
                        if (!combinedImageData[roomtypeid]) {
                          const firstImage = roomGallery.gallery[0];
                          combinedImageData[roomtypeid] = [firstImage];
                          console.log(`Added image for roomtype ${roomtypeid}`);
                        }
                      }
                    });
                  }
                });
              }
              
              console.log('Combined image data:', Object.keys(combinedImageData).length, 'rooms');
              setRoomImages(combinedImageData);
            } else {
              console.log('No image data available');
              setRoomImages({});
            }
          } else {
            console.log('Image API request failed');
            setRoomImages({});
          }
        } catch (imageError) {
          console.error('Error fetching room images:', imageError);
          setRoomImages({});
        }
      } else {
        setAvailabilityData({ success: false, error: { code: 0, message: 'No availability found' } });
      }
      
    } catch (error) {
      console.error('Error checking availability:', error);
      setError('Failed to fetch availability data');
    } finally {
      setIsLoadingAvailability(false);
      setIsFetching(false);
    }
  }, [widgetConfig.bbid, config.api.availabilityWorker, isFetching]);

  const handleDateClick = (date) => {
    if (!date || isDateDisabled(date)) return;
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setDateRange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
      console.log('Check-in date selected, waiting for checkout date');
    } else {
      if (date <= selectedStartDate) {
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setDateRange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
        console.log('New check-in date selected, waiting for checkout date');
      } else {
        setSelectedEndDate(date);
        const start = `${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`;
        const end = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setDateRange(`${start} - ${end}`);
        setShowCalendar(false);
        
        console.log('Checkout date selected, fetching availability for:', start, 'to', end);
        fetchAvailability(start, end);
      }
    }
  };

  const getNightsCount = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const diffTime = Math.abs(selectedEndDate - selectedStartDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateTotalPrice = (room) => {
    if (!selectedStartDate || !selectedEndDate) return null;
    const nights = getNightsCount();
    
    // Use the same fallback pricing system from App.js
    const fallbackPricing = {
      'private': 3500, 'game lodge': 4200, 'tholo': 3800, 'sitrah': 4100,
      'mingwe': 3900, 'tebuah': 4300, 'thula': 4500, 'safari': 2800,
      'bush': 2400, 'milkwood': 2600, 'tswene': 2300, 'bona kgole': 2200,
      'livingstone': 2500, 'kwalata': 2700, 'lodge': 1800, 'cottage': 1600,
      'view': 1900, 'hadeda': 1700, 'gecko': 1500, 'kubu': 1400,
      'bushdream': 1600, 'kanaan': 1750, 'rock': 1550, 'valley': 1650,
      'nyala': 1850, 'bateleur': 2100, 'hinda': 2000, 'mbekka': 1950,
      'mabalingwe': 200, 'default': 2000
    };
    
    const roomName = room.rtname.toLowerCase();
    let baseRate = fallbackPricing.default;
    
    for (const [keyword, rate] of Object.entries(fallbackPricing)) {
      if (roomName.includes(keyword) && keyword !== 'default' && keyword !== 'mabalingwe') {
        baseRate = rate;
        break;
      }
    }
    
    if (roomName.includes('mabalingwe')) {
      baseRate += fallbackPricing.mabalingwe;
    }
    
    const variation = (room.rtid % 5) * 50 - 100;
    baseRate += variation;
    
    return baseRate * nights;
  };

  const openBookingIframe = (bbid = null, bbrtid = null) => {
    if (!selectedStartDate || !selectedEndDate) {
      alert('Please select both check-in and check-out dates first.');
      return;
    }
    
    const startDateStr = `${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`;
    const nights = getNightsCount();
    
    const propertyId = bbid || config.booking.propertyId;
    const roomTypeId = bbrtid ? `&bbrtid=${bbrtid}` : '';
    
    const url = `${config.booking.baseUrl}/${propertyId}?nbid=${config.booking.nbid}&startdate=${startDateStr}&nights=${nights}${roomTypeId}`;
    
    console.log('Opening booking iframe with URL:', url);
    setIframeUrl(url);
    setShowIframe(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()}`;
  };

  const formatDateRange = (range) => {
    if (!range) return '';
    if (range.includes(' - ')) {
      const [start, end] = range.split(' - ');
      return `${formatDate(start)} - ${formatDate(end)}`;
    } else {
      return formatDate(range);
    }
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (date < today) return true;
    
    // Check if date is blocked (no availability)
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (blockedDates[dateKey]) {
      console.log('Date disabled (blocked):', dateKey);
      return true;
    }
    
    return false;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
           (selectedEndDate && date.getTime() === selectedEndDate.getTime());
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate || !date) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      dayDate.setHours(0, 0, 0, 0);
      days.push(dayDate);
    }
    
    return days;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Check availability for the current month
  const checkMonthAvailability = useCallback(async (month, year) => {
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
    
    // Skip if we already have data for this month
    if (monthAvailability[monthKey]) return;
    
    console.log('Fetching month availability for:', monthKey);
    
    try {
      // Get first and last day of the month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Ensure start date is not before today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = firstDay < today ? today : firstDay;
      
      const requestBody = {
        bbid: widgetConfig.bbid,
        startdate: startDate.toISOString().split('T')[0],
        enddate: lastDay.toISOString().split('T')[0]
      };

      console.log(`Month availability request:`, requestBody);

      const response = await fetch(config.api.availabilityWorker, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log(`Month availability response status:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`Month availability data:`, data);
        
        if (data.success && data.data?.roomtypes) {
          const combinedData = {
            success: true,
            data: {
              roomtypes: data.data.roomtypes.map(room => ({
                ...room,
                bbid: widgetConfig.bbid
              }))
            }
          };
          
          setMonthAvailability(prev => ({
            ...prev,
            [monthKey]: combinedData
          }));
          
          // Process blocked dates
          console.log('Processing roomtypes for blocked dates:', combinedData.data.roomtypes);
          const blocked = {};
          const dateAvailability = {};
          
          // Collect availability for each date
          combinedData.data.roomtypes.forEach(room => {
            console.log('Processing room:', room.rtid, 'availability:', room.availability);
            if (room.availability && Array.isArray(room.availability)) {
              room.availability.forEach((day, index) => {
                const date = new Date(firstDay);
                date.setDate(firstDay.getDate() + index);
                const dateKey = date.toISOString().split('T')[0];
                
                if (!dateAvailability[dateKey]) {
                  dateAvailability[dateKey] = [];
                }
                dateAvailability[dateKey].push(day.roomsfree);
                console.log(`Date ${dateKey}, day ${index}: roomsfree = ${day.roomsfree}`);
              });
            }
          });
          
          console.log('Date availability summary:', dateAvailability);
          
          // Block dates if ANY room is unavailable
          Object.keys(dateAvailability).forEach(dateKey => {
            const anyRoomUnavailable = dateAvailability[dateKey].some(available => !available);
            console.log(`Date ${dateKey}: anyRoomUnavailable = ${anyRoomUnavailable}`);
            if (anyRoomUnavailable) {
              blocked[dateKey] = true;
              console.log('Blocked date:', dateKey);
            }
          });
          
          console.log('Setting blocked dates:', blocked);
          setBlockedDates(prev => ({ ...prev, ...blocked }));
        }
      } else {
        console.log('Month availability response not ok:', response.status);
      }
    } catch (error) {
      console.log('Could not fetch month availability:', error);
    }
  }, [monthAvailability, widgetConfig.bbid, config.api.availabilityWorker]);

  // Check availability when month changes
  useEffect(() => {
    checkMonthAvailability(currentMonth.getMonth(), currentMonth.getFullYear());
  }, [currentMonth, checkMonthAvailability]);

  // Debug blocked dates state
  useEffect(() => {
    console.log('Current blocked dates:', blockedDates);
  }, [blockedDates]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);

  // Widget-specific styles
  const widgetStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    backgroundColor: widgetConfig.theme === 'dark' ? '#1f2937' : '#ffffff',
    color: widgetConfig.theme === 'dark' ? '#f9fafb' : '#374151',
    minHeight: widgetConfig.embedded ? 'auto' : '100vh',
    padding: widgetConfig.embedded ? '0' : '0'
  };

  return (
    <div style={widgetStyle}>
      <div className="check-availability-widget">
        <div className="ca-app">

          {/* Date Picker Section */}
          <section className="full-width-section">
            <div className="full-width-content">
              <h2 style={{ fontSize: '1.125rem' }}>{config.datePicker?.title || 'Select Date Range'}</h2>
              <div className="ca-date-picker-container">
                <div className="ca-modern-date-picker" ref={calendarRef}>
                  <div 
                    className="ca-date-input-wrapper mb-2"
                    onClick={() => setShowCalendar(!showCalendar)}
                  >
                    <input
                      type="text"
                      className="ca-modern-date-input"
                      value={formatDateRange(dateRange)}
                      readOnly
                      placeholder={config.datePicker?.placeholder || 'Select date range...'}
                    />
                    <div className="ca-calendar-icon">📅</div>
                    {selectedStartDate && (
                      <button 
                        className="ca-clear-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStartDate(null);
                          setSelectedEndDate(null);
                          setDateRange('');
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  {showCalendar && (
                    <div 
                      className="ca-fixed ca-inset-0 ca-bg-black ca-bg-opacity-50 flex items-center justify-center z-50"
                      onClick={() => {
                        setShowCalendar(false);
                        // Clear cached month data when calendar is closed
                        setMonthAvailability({});
                        setBlockedDates({});
                        console.log('Calendar closed - cleared cached data');
                      }}
                    >
                      <div 
                        className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                          </button>
                          <h3 className="text-lg font-semibold">
                            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                          </h3>
                          <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="text-center text-sm font-medium text-gray-500 py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1 h-64">
                          {days.map((day, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                if (day && !isDateDisabled(day)) {
                                  handleDateClick(day);
                                  if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                                    setDateRange(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`);
                                  } else {
                                    const start = `${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`;
                                    const end = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                                    setDateRange(`${start} - ${end}`);
                                    setShowCalendar(false);
                                  }
                                }
                              }}
                              disabled={isDateDisabled(day)}
                              className={`
                                p-2 text-sm rounded hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center h-8
                                ${!day ? 'invisible' : ''}
                                ${isDateDisabled(day) ? 'text-gray-300 cursor-not-allowed bg-gray-100' : 'text-gray-700 hover:text-blue-600'}
                                ${isDateSelected(day) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                                ${isDateInRange(day) && !isDateSelected(day) ? 'bg-blue-100 text-blue-700' : ''}
                              `}
                            >
                              {day ? day.getDate() : ''}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
                              {/* Date Summary */}
                {selectedStartDate && selectedEndDate && (
                  <div className="rounded-lg p-1 mb-6 mt-1">
                    <div className="flex items-center justify-center space-x-4 text-sm">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span className="text-blue-800 font-medium">
                        {formatDate(`${selectedStartDate.getFullYear()}-${String(selectedStartDate.getMonth() + 1).padStart(2, '0')}-${String(selectedStartDate.getDate()).padStart(2, '0')}`)} - {formatDate(`${selectedEndDate.getFullYear()}-${String(selectedEndDate.getMonth() + 1).padStart(2, '0')}-${String(selectedEndDate.getDate()).padStart(2, '0')}`)}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span className="text-green-800 font-medium">
                        {getNightsCount()} night{getNightsCount() !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoadingAvailability && (
                <div className="text-center py-6 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="inline-flex items-center mb-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-800 font-medium text-lg">
                      Checking room availability...
                    </span>
                  </div>
                  <p className="text-blue-600 text-sm">
                    This may take a few seconds.
                  </p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span className="text-red-800 font-medium text-lg">Error loading availability data</span>
                    </div>
                  </div>
                  <p className="text-red-700 text-sm">We couldn't connect to the availability service.</p>
                </div>
              )}

              {/* Availability Display */}
              {availabilityData && !isLoadingAvailability && !error && (
                <div>
                  {/* Simplified room display for widget */}
                  {availabilityData.success && availabilityData.data?.roomtypes && (
                    <div>
                      <h4>Rooms on Property</h4>
                      <div className="rooms-list">
                        {availabilityData.data.roomtypes
                          .sort((a, b) => {
                            const aAvailable = a.availability && Array.isArray(a.availability) && a.availability.length > 0;
                            const bAvailable = b.availability && Array.isArray(b.availability) && b.availability.length > 0;
                            return bAvailable - aAvailable; // Available rooms first
                          })
                          .map((room, index) => {
                          const isAvailable = room.availability && Array.isArray(room.availability) 
                            ? room.availability.every(day => day.roomsfree === true)
                            : true;

                          // Get image data for this room if available
                          const roomImageData = roomImages[room.rtid];
                          const primaryImage = roomImageData && roomImageData.length > 0 ? roomImageData[0] : null;
                          
                          // Get image URL with fallbacks for different formats
                          const getImageUrl = (image) => {
                            if (!image) return null;
                            // Try different possible URL formats, prioritize medium for better visibility
                            return image.medium || image.small || image.big || image.url || image.src || image;
                          };
                          
                          const imageUrl = primaryImage ? getImageUrl(primaryImage) : null;

                          return (
                            <div key={index} className={`room-card ${!isAvailable ? 'unavailable' : ''}`} onClick={() => isAvailable && openBookingIframe(room.bbid, room.rtid)}>
                              <div className="room-content-wrapper">
                                {/* Room Image Section */}
                                <div className="room-image-section">
                                  {imageUrl ? (
                                    <img 
                                      src={imageUrl} 
                                      alt={room.rtname || 'Room'} 
                                      className="room-image"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                  ) : (
                                    <div className="room-image-placeholder">
                                      <span>📷</span>
                                      <p>No image available</p>
                                    </div>
                                  )}
                                  {imageUrl && (
                                    <div className="room-image-fallback" style={{ display: 'none' }}>
                                      <span>📷</span>
                                      <p>Image unavailable</p>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Room Info Section */}
                                <div className="room-info-section">
                                  <div className="room-header">
                                    <h5 className="room-title">{String(room.rtname || 'Unknown Room')}</h5>
                                    <span className={`availability-badge ${isAvailable ? 'available' : 'unavailable'}`}>
                                      {isAvailable ? 'Available' : 'Unavailable'}
                                    </span>
                                  </div>
                                  
                                  <div className="room-info">
                                    <p><strong>Property:</strong> {String(room.propertyName || `Property ${room.bbid}` || 'Unknown Property')}</p>
                                  </div>
                                  
                                  {isAvailable && (
                                    <div className="room-booking-hint" style={{ 
                                      marginTop: '10px', 
                                      padding: '8px 8px 0 8px', 
                                      fontSize: '0.9em',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <div style={{ 
                                        color: widgetConfig.theme === 'dark' ? '#f9fafb' : 'black', 
                                        fontSize: '1.5rem',
                                        fontWeight: '600'
                                      }}>
                                        {calculateTotalPrice(room) ? `R${calculateTotalPrice(room).toFixed(0)}` : 'Price TBA'}
                                      </div>
                                      <div style={{
                                        color: widgetConfig.primaryColor,
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        💳 <span style={{ borderBottom: '1px solid currentColor', fontSize: '1.1em' }}>Book this room</span>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M3 9L9 3M9 3H4M9 3V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Booking Iframe Modal */}
          {showIframe && (
            <div 
              className="ca-fixed ca-inset-0 ca-bg-black ca-bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowIframe(false)}
            >
              <div 
                className="bg-white rounded-lg shadow-xl" 
                style={{ width: '1200px', maxWidth: '95vw', height: '85vh', position: 'relative', overflow: 'hidden' }}
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  src={iframeUrl}
                  title="NightsBridge Booking"
                  allow="payment"
                  style={{ 
                    border: 'none', 
                    width: '100%', 
                    height: '100%',
                    borderRadius: '8px'
                  }}
                />
                
                <button
                  onClick={() => setShowIframe(false)}
                  className="absolute p-2 bg-white hover:bg-gray-100 rounded-full shadow-lg"
                  style={{ 
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '40px', 
                    height: '40px',
                    zIndex: '99999',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Widget;
